/**
 * AccountChoice — "is your vehicle registered?", asked once, up front.
 *
 * "Registered" is a registered VEHICLE (licence plate + PIN), not an account
 * sign-in. The ids and the stored answer keep their historical names
 * ('account' / ACCOUNT_CHOICE.ACCOUNT) so routing and saved state are unchanged.
 *
 * ── WHY IT SITS HERE ─────────────────────────────────────────
 *
 * Charger profile → [Get Started] → THIS → (connector choice, if any) → …
 *
 * It has to come before session creation, not after. A guest session is
 * created with `user_id = NULL`, and there is no code path in sw-ev-api that
 * sets `user_id` on an existing `ev_sessions` row — a guest session can never
 * be claimed later. Asking after the session exists would mean signing in
 * either orphans that session or opens a second one on the same connector.
 *
 * ── WHAT EACH ANSWER DOES ────────────────────────────────────
 *
 *   "I am registered"
 *       → records the choice and goes to the plate screen. NO sign-in: a
 *         registered vehicle is authorised for this charge by its plate and
 *         PIN, which is vehicle verification, not account authentication. It
 *         grants no access to the owner's history, wallet, saved payment
 *         details or profile. This used to send the driver to
 *         /auth?redirect=… — an email + OTP funnel between the driver and
 *         the charge. Already signed in? We skip straight past.
 *
 *   "Continue as guest"
 *       → records the choice and skips the plate screen entirely. Guests
 *         still choose a budget: a pre-authorisation is always required, so
 *         "straight to the card" is not a thing the payment flow supports.
 *
 * ── WHAT IT MUST NOT DO ──────────────────────────────────────
 *
 * It creates no session and selects no connector. It only records an answer.
 * Browser-back from here must never leave a stray session behind — the same
 * rule ConnectorSelection follows.
 */
import React, { useCallback, useEffect } from 'react';
import { EvHeader, ActionButtonWrapper, Notice, BackButton } from '../../index.js';
import { useSession } from '../../../context/SessionContext.jsx';
import { useAuth } from '../../../context/AuthContext.jsx';
import { ACCOUNT_CHOICE } from '../../../constants/accountChoice.js';
import { useCancelAttempt } from '../../../hooks/useCancelAttempt.jsx';
import './AccountChoice.less';

const AccountChoice = ({ handleIncreaseStep, handleDecreaseStep }) => {
  const { state, update, clearAuthRoundTrip } = useSession();
  const { cancelNow, reportResult } = useCancelAttempt();
  const { isAuthenticated, isBooting } = useAuth();

  // Already signed in: the question is answered by definition. Record it and
  // move on rather than making a signed-in driver assert who they are.
  useEffect(() => {
    if (isBooting || !isAuthenticated) return;
    if (state.accountChoice === ACCOUNT_CHOICE.ACCOUNT) return;
    update({ accountChoice: ACCOUNT_CHOICE.ACCOUNT });
  }, [isBooting, isAuthenticated, state.accountChoice, update]);

  // Back from /auth, authenticated: the round trip is over, so drop the
  // extended TTL and let the ordinary pre-charging expiry apply again.
  useEffect(() => {
    if (isBooting) return;
    if (!isAuthenticated) return;
    if (state.authRoundTripStartedAt) clearAuthRoundTrip();
  }, [isBooting, isAuthenticated, state.authRoundTripStartedAt, clearAuthRoundTrip]);

  useEffect(() => {
    if (isBooting || !isAuthenticated) return;
    handleIncreaseStep();
  }, [isBooting, isAuthenticated, handleIncreaseStep]);

  // Record the answer only; the plate screen follows. See the header for why
  // this no longer routes through /auth.
  const chooseAccount = useCallback(() => {
    update({ accountChoice: ACCOUNT_CHOICE.ACCOUNT });
  }, [update]);

  // Record the answer only. Advancing is left to the effect below.
  //
  // Calling handleIncreaseStep() here as well looked right and was wrong:
  // update() is setState, so it does not apply until the next render, while
  // handleIncreaseStep is App's nextStep — a useCallback closing over
  // session.state.accountChoice from the PREVIOUS render, still null. The
  // guest-skip branch therefore never matched and the driver was routed to
  // CHARGE_SETUP: the plate screen this whole step exists to spare them.
  //
  // Unit tests could not see it, because they seed accountChoice before mount
  // and the closure already holds it. Only the real click ordering shows it.
  const chooseGuest = useCallback(() => {
    update({ accountChoice: ACCOUNT_CHOICE.GUEST });
  }, [update]);

  // Advance once the answer is actually in state, so App's routing reads the
  // value the driver just chose. Mirrors the signed-in auto-advance above
  // rather than introducing a second mechanism.
  //
  // A signed-in driver is advanced by the effect above, so the account answer
  // advances here only for a driver who is not signed in -- otherwise both
  // effects would fire and skip a step.
  useEffect(() => {
    if (state.accountChoice === ACCOUNT_CHOICE.GUEST) {
      handleIncreaseStep();
      return;
    }
    if (state.accountChoice === ACCOUNT_CHOICE.ACCOUNT && !isBooting && !isAuthenticated) {
      handleIncreaseStep();
    }
  }, [state.accountChoice, isBooting, isAuthenticated, handleIncreaseStep]);

  const actions = [
    // "Registered" means a registered VEHICLE (plate + PIN), not an account
    // sign-in: nothing on this path authenticates the driver.
    { id: 'account', label: 'I am registered', type: 'btn-dark' },
    // Outline-dark, as other screens' secondary actions (e.g. Cancel): clearly
    // actionable. The grey outline-secondary read as disabled.
    { id: 'guest', label: 'Continue as guest', type: 'btn-outline-dark' },
  ];

  // Header Back: to the charger profile (STEP_BACK maps this step to
  // EV_PROFILE). Nothing is created on this screen, so normally there is no
  // attempt to leave behind. Should one exist anyway (a restored state), leave
  // it the way every other abandonment does -- cancelled server-side, which
  // also resets the wizard to the charger profile -- never forgotten locally.
  const goBack = useCallback(() => {
    if (state.sessionUid) {
      cancelNow().then(reportResult);
      return;
    }
    handleDecreaseStep();
  }, [state.sessionUid, cancelNow, reportResult, handleDecreaseStep]);

  const onClickAction = (actionId) => {
    if (actionId === 'account') return chooseAccount();
    if (actionId === 'guest') return chooseGuest();
  };

  return (
    <div className="ev-content-wrapper ac-screen">
      <EvHeader headerAction={{ label: <BackButton />, action: goBack }} />

      <div className="ac-wrapper">
        <h2 className="ac-title">Is your vehicle registered with GreenCharge?</h2>
        <p className="ac-subtitle">
          Registered vehicles are recognised by their licence plate and PIN.
          You can always continue as a guest.
        </p>

        <ul className="ac-benefits">
          <li>Your vehicle&rsquo;s registered rate applies automatically</li>
          <li>Verified with your plate and vehicle PIN</li>
          <li>Receipts go to the vehicle&rsquo;s registered owner</li>
        </ul>

        <Notice variant="info" role="status" className="ac-note">
          You’ll confirm your charging details before charging starts.
        </Notice>
      </div>

      <ActionButtonWrapper actions={actions} onClickAction={onClickAction} />
    </div>
  );
};

export default AccountChoice;
